import React, { useEffect, useState } from "react";
import {
  ArrowLeftOutlined,
  EditOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, message, notification, Popconfirm } from "antd";
import GenericModal from "../../../../../Components/Modals/GenericModal";
import BrandIcon from "../../../../../Assets/Icons/local_gas_station.svg";
import entityIcon from "../../../../../Assets/Icons/solar_fuel-bold.svg";
import {
  getFuelBrandById,
  updateFuelBrandStatus,
} from "../../../../../API/Admin/SystemConfiguration/FuelBrands";
import "./style.css";
import InitialsAvatar from "../../../../../Components/InitialsAvatar";
import usePageAccess from "../../../../../Utlis/usePageAccess";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const FuelBrandDetails = () => {
  const { canEdit } = usePageAccess("system-configuration");
  const navigate = useNavigate();
  const location = useLocation();
  const brandId = location.state?.id;

  const [isInactive, setIsInactive] = useState(true);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [brand, setBrand] = useState({
    brandName: "-",
    legalEntityName: "-",
    avatar: null,
  });

  useEffect(() => {
    if (!brandId) return;

    const fetchBrand = async () => {
      try {
        const res = await getFuelBrandById(brandId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setBrand({
            id: data.id,
            brandName: data.brand_name || "-",
            legalEntityName: data.legalentity_name || "-",
            avatar: resolveAvatarUrl(data.avatar),
          });
          setIsInactive((data.status || "").toLowerCase() !== "active");
          setReason(data.inactive_reason || "");
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load fuel brand."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel brand."),
        );
      }
    };

    fetchBrand();
  }, [brandId]);

  const handleBack = () => {
    navigate("/system-configuration", { state: { activeTab: "Fuel Brands" } });
  };

  const handleToggleStatus = () => {
    if (!isInactive) {
      setIsStatusModalOpen(true);
    }
  };

  const handleConfirmDeactivate = async (enteredReason) => {
    try {
      const res = await updateFuelBrandStatus(brandId, {
        status: "inactive",
        inactive_reason: enteredReason,
      });

      if (Math.floor(res?.status / 100) === 2) {
        setIsInactive(true);
        setReason(enteredReason);

        notification.warning({
          message: "Status Deactivated",
          description:
            res?.data?.message || "Fuel brand has been deactivated successfully!",
          placement: "topRight",
        });
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to deactivate fuel brand."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to deactivate fuel brand."),
      );
    } finally {
      setIsStatusModalOpen(false);
    }
  };

  const handleActivateStatus = async () => {
    try {
      const res = await updateFuelBrandStatus(brandId, { status: "active" });

      if (Math.floor(res?.status / 100) === 2) {
        setIsInactive(false);
        setReason("");

        notification.success({
          message: "Status Reactivated",
          description:
            res?.data?.message || "Fuel brand has been reactivated successfully!",
          placement: "topRight",
        });
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to reactivate fuel brand."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to reactivate fuel brand."),
      );
    }
  };
  const handleEdit = () => {
    navigate("/edit-fuel-brand", { state: { id: brand.id } });
  };

  return (
    <div className="fuel-detail">
      <div className="top-section">
        <div className="back-btn">
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back
          </Button>
        </div>

        <div className="header-row">
          <div className="title">Fuel Brand Details</div>

          <div className="btn-group">
            {canEdit && (
              <>
                {!isInactive ? (
                  <Button
                    className="locked-btn"
                    icon={<LockOutlined />}
                    onClick={handleToggleStatus}
                  >
                    Inactivate
                  </Button>
                ) : (
                  <Popconfirm
                    title="Are you sure you want to reactivate?"
                    onConfirm={handleActivateStatus}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      className="unlocked-btn"
                      icon={<LockOutlined />}
                      style={{
                        color: "#16a34a",
                        backgroundColor: "#16a34a1a",
                      }}
                    >
                      Reactivate
                    </Button>
                  </Popconfirm>
                )}

                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                  Edit Fuel Brand
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {isInactive && (
        <div className="locked-info">
          <div className="label">
            Inactive Reason
            <div className="reason"> {reason}</div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {brand.avatar ? (
                  <img src={brand.avatar} alt="Profile Avatar" />
                ) : (
                  <InitialsAvatar
                    name={brand.brandName}
                    style={{ width: "100%", height: "100%", fontSize: 40 }}
                  />
                )}
              </div>
            </div>

            <div className="profile-body">
              <div className="company-name">{brand.brandName}</div>
              <div className="company-id">
                {" "}
                Brand / ID: {brand.id ?? "-"}
              </div>

              <div>
                <span
                  className={`badge-status ${isInactive ? "inactive" : "active"}`}
                >
                  {isInactive ? "Inactive" : "Active"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="right-info">
            <div className="info-grid">
              <div className="item">
                <div className="card-wrapper">
                  <div className="icon">
                    <img src={BrandIcon} alt="Brand Icon" />
                  </div>
                  <div className="info-label">Brand Nmae</div>
                </div>
                <div className="info-value">{brand.brandName}</div>
              </div>

              <div className="item">
                <div className="card-wrapper">
                  <div className="icon">
                    <img src={entityIcon} alt="Entity Icon" />
                  </div>
                  <div className="info-label">Legal Entity Name</div>
                </div>
                <div className="info-value">{brand.legalEntityName}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GenericModal
        open={isStatusModalOpen}
        title="Reacivate Status"
        subtitle="Please provide a reason for reactivating this fuel brand"
        showReason={true}
        confirmText="Confirm Reactivation"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsStatusModalOpen(false)}
        onConfirm={handleConfirmDeactivate}
      />
    </div>
  );
};

export default FuelBrandDetails;
